<template>
  <div class="data-exports">
    <WdsStack gap-md align-center class="tw-mt-5 tw-mb-4">
      <h2 class="tw-my-0">
        {{ $t('Data exports') }}
      </h2>
      <div>
        <WdsLabel xs warning subtle>
          Beta
          <WdsIcon
            :size="13"
            weight="thin"
            :name="WdsIconNameEnum.LIGHT"
            class="!tw-inline-block"
          />
        </WdsLabel>
      </div>
    </WdsStack>
    <WdsText>
      <h4>Accounts</h4>
      <WdsStack gap-sm direction-column>
        <WdsSelect
          v-model="selectedAccounts"
          multi-select
          searchable
          :list="accounts"
          placeholder="Select the accounts you want to retrieve data from"
        >
          <template #option="{ option }">
            <WdsStack gap-sm>
              <WdsAvatar xs :initials="option.name" :path="option.avatarPath" />
              <WdsText tertiary>{{ option.name }}</WdsText>
            </WdsStack>
          </template>
        </WdsSelect>
        <WdsCheckbox
          :checked="accounts?.length === selectedAccounts.length"
          @update:checked="toggleAccounts"
        >
          Select all accounts
        </WdsCheckbox>
      </WdsStack>
    </WdsText>

    <h4>Query Endpoints</h4>
    <WdsSelect v-model="selectedQuery" :list="selectAbleQueries" searchable>
      <template #option="{ option }">
        <WdsText :text="option.label" :sub-text="option.value.description" />
      </template>
    </WdsSelect>
  </div>

  <div v-if="selectedQuery !== null" class="field-table">
    <WdsStack direction-row align-center gap-md>
      <h4>Select fields needed in export</h4>
      <WdsButton v-show="numArgsFilters > 0" primary @click="toggleFilters">Filters</WdsButton>
    </WdsStack>
    <GraphqlDataTableFilters
      v-model:filters="activeFilterValues"
      v-model:show="filtersOpen"
      display-all-args
      :query="dynamicQuery"
      @number-of-filters="numArgsFilters = $event"
    />
    <WdsTabs v-model:active-tab-key="activeQueryDataTabKey">
      <WdsTab
        v-for="(fields, name) in tableData"
        :key="name"
        :tab-key="name"
        :title="fieldToReadAble(name)"
      >
        <BaseDataTable
          :entries="fields"
          :columns="fieldColumns"
          :pre-selected-rows="selectedFields[name]"
          multi-select
          row-hover
          preserve-selected-rows
          @rows-selected="rowsSelected(name, $event)"
        />
      </WdsTab>
    </WdsTabs>
  </div>

  <WdsStack v-if="selectedQuery !== null" gap-sm>
    <WdsButton primary :disabled="numSelectedFields < 1" @click="queryHeaderData">
      Show example data
    </WdsButton>
    <WdsButton primary :disabled="numSelectedFields < 1" @click="exportData">
      Export all data as csv
    </WdsButton>
    <WdsButton primary :disabled="numSelectedFields < 1" @click="openRecurringExportModal = true">
      Save as recurring export
    </WdsButton>
  </WdsStack>
  <WdsModal
    :show="openRecurringExportModal"
    title="Recurring export"
    @cancel="openRecurringExportModal = false"
  >
    <WdsStack gap-md direction-column>
      <WdsInput
        placeholder="WeeklyContractExport"
        label="Export name. Will be used as the file(s) names"
      />

      <WdsSegmentedControl
        label="Export file as"
        :options="[
          { optionKey: 'csv', title: 'CSV Sheets' },
          { optionKey: 'excel', title: 'Excel Sheet' },
          { optionKey: 'json', title: 'JSON' },
        ]"
      />

      <WdsStack direction-column gap-sm>
        <WdsInput
          v-model="schedule"
          label="Schedule for export"
          placeholder="5 4 * * *"
          :info="scheduleHumanReadable"
        />
        <WdsText>
          The export schedule is defined using the cron job format, which you can learn more about
          by clicking
          <WdsButtonText href="https://crontab.guru/" target="_blank">here</WdsButtonText>
          . Please note that the schedule operates in UTC time, so you may want to compare UTC with
          your local time
          <WdsButtonText href="https://dateful.com/convert/utc" target="_blank">here</WdsButtonText>
          . If you're not familiar with cron job formatting, you can use one of the following handy
          shortcuts to schedule the export:
          <ol>
            <li>
              Once a
              <WdsButtonText @click="schedule = '@weekly'">week</WdsButtonText>
            </li>
            <li>
              Once a
              <WdsButtonText @click="schedule = '@daily'">day</WdsButtonText>
            </li>
            <li>
              Once an
              <WdsButtonText @click="schedule = '@hourly'">hour</WdsButtonText>
            </li>
            <li>
              Every
              <WdsButtonText @click="schedule = '@monthly'">month</WdsButtonText>
            </li>
          </ol>
        </WdsText>
      </WdsStack>
      <WdsInput
        placeholder="email@someplace.com"
        label="Send export file to target email"
        info="For now we only send the exports via email. If you have other needs please let us know by reaching out to us, and we can figure something out."
      />
    </WdsStack>
  </WdsModal>

  <div v-if="Object.keys(queryHeaderDataResults).length > 0" class="query-table">
    <h4>Example data</h4>
    <WdsTabs>
      <WdsTab
        v-for="(results, name) in queryHeaderDataResults"
        :key="name"
        :tab-key="name"
        :title="fieldToReadAble(name)"
      >
        <BaseDataTable :entries="results" :columns="headerDataColumns[name]" />
      </WdsTab>
    </WdsTabs>
  </div>
</template>

<script lang="ts" setup>
import { computed, markRaw, reactive, Ref, ref, watch } from 'vue'
import {
  WdsAvatar,
  WdsButton,
  WdsButtonText,
  WdsCheckbox,
  WdsIcon,
  WdsIconNameEnum,
  WdsInput,
  WdsLabel,
  WdsModal,
  WdsSegmentedControl,
  WdsSelect,
  WdsStack,
  WdsTab,
  WdsTabs,
  WdsText,
} from '@worksome/wds-components'
import gql from 'graphql-tag'
import uniqBy from 'lodash/uniqBy'
import cronstrue from 'cronstrue'
import 'cronstrue/locales/fr'
import 'cronstrue/locales/da'
import 'cronstrue/locales/de'
import 'cronstrue/locales/en'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { dotAccess } from '@/utils/tools'
import BaseDataTable from '@/components/BaseDataTable/BaseDataTable.vue'
import { provider } from '@/app-bootstrapping/SetUpApollo'
import { dataExport, SheetDescriber, TableDescriber } from '@/utils/apiDataExports'
import { TdText } from '@/components/BaseDataTable/Cells'
import GraphqlDataTableFilters from '@/components/GraphqlDataTable/GraphqlDataTableFilters.vue'
import {
  useCompanyAccounts,
  useIntrospectionTypes,
  useQueryType,
} from '@/components/GraphqlDataTable/composables'
import {
  convertArgTypeToTypeStr,
  fieldToReadAble,
  getFieldTypeLeaf,
} from '@/components/GraphqlDataTable/tools'
import { ListItem } from '@/components/Workflows/Pages/Workflow/workflow'
import {
  CompanyAccountFragment,
  FieldTypeFragment,
  OrganisationAccountFragment,
} from '@/gql/graphql'
import { useFragment } from '@/gql'
import {
  FieldTypeFragmentDef,
  InputValueFragmentDef,
  TypeRefFragmentDef,
} from '@/components/GraphqlDataTable/queries'

const clientId = 'publicApiCustomFields'

const { accounts } = useCompanyAccounts()

// Reactive reference to store selected accounts
const selectedAccounts: Ref<(ListItem & (CompanyAccountFragment | OrganisationAccountFragment))[]> =
  ref([])

// Function to toggle the selection of all accounts
const toggleAccounts = (event: boolean) => {
  // If 'event' is true, select all accounts; otherwise, deselect all
  selectedAccounts.value = event ? accounts.value : []
}

// List of query names to exclude (blacklist)
const blackList = [
  'workflows',
  'workflowVariables',
  'approvalRules',
  'approvals',
  'approvers',
  'approvalApprovables',
  'approvalStates',
  'inheritedCustomFields',
]

// Computed property to create a lookup table for types based on their names
const { typeLookup, queryType } = useIntrospectionTypes()

// Computed property to filter valid GraphQL queries
const validQueries = computed(() => {
  if (!queryType.value?.fields) return [] // Return empty array if data is still loading

  return queryType.value?.fields
    .map((f) => useFragment(FieldTypeFragmentDef, f))
    .filter(
      ({ type, args, name }) =>
        // Exclude blacklisted queries
        !blackList.includes(name) &&
        // For us to support a query we expect it to return a paginator
        // It also should take a list of accounts as an argument,
        // and lastly none of the arguments should require a specific ID

        // As we add more support for more queries we can expand this
        useFragment(TypeRefFragmentDef, type)?.ofType?.name?.includes('Paginator') &&
        useFragment(InputValueFragmentDef, args)?.some(
          ({ name: argName }) => argName === 'accounts',
        ) &&
        !useFragment(InputValueFragmentDef, args)?.some(
          ({ type: argType }) => useFragment(TypeRefFragmentDef, argType).name === 'ID',
        ),
    )
})

// Computed property to create options for selectable queries
const selectAbleQueries = computed(() =>
  validQueries.value.map((query) => ({
    // Format the query name to a human-readable label
    label: fieldToReadAble(query.name),
    value: query,
  })),
)

// Reactive reference to store the selected query
const selectedQuery: Ref<{ label: string; value: FieldTypeFragment } | null> = ref(null)
const activeFilterValues = reactive({})
const numArgsFilters = ref(0) // Number of argument filters from the filters tab

// Computed property to get the data type of the selected query
const queryData = computed(() => {
  if (selectedQuery.value === null) return null // Return null if no query is selected

  // Extract the name of the type returned by the selected query
  const {
    value: { type },
  } = selectedQuery.value

  const { ofType } = useFragment(TypeRefFragmentDef, type)

  // Return the corresponding type from the type lookup
  return !!ofType && typeof ofType.name === 'string' ? typeLookup.value[ofType.name] : null
})

// Computed property to get the name of the data type for the selected query
const selectedQueryTypeName = computed(() => {
  if (!queryData.value) return ''
  // Find the 'data' field within the query data
  const dataField = queryData.value?.fields
    ?.map((field) => useFragment(FieldTypeFragmentDef, field))
    .find((field) => field.name === 'data')
  if (!dataField) return ''
  const type = useFragment(TypeRefFragmentDef, dataField.type)
  // Return the name of the innermost type of the 'data' field
  return getFieldTypeLeaf(type).name
})

// Array defining the columns for the field selection table
const fieldColumns = [
  {
    title: 'Column Name',
    key: 'name',
  },
  {
    title: 'Description',
    key: 'description',
  },
]

// Reactive object to store selected fields for each type
const selectedFields = reactive({})
const numSelectedFields = computed(() => Object.keys(selectedFields).length)
const activeQueryDataTabKey: Ref<string | undefined> = ref(undefined)
watch(selectedQueryTypeName, () => {
  activeQueryDataTabKey.value = selectedQueryTypeName.value ?? undefined
  for (const key in selectedFields) {
    if (selectedFields.hasOwnProperty(key)) {
      delete selectedFields[key]
    }
  }
  for (const key in activeFilterValues) {
    if (activeFilterValues.hasOwnProperty(key)) {
      delete activeFilterValues[key]
    }
  }
})

// Function to update selected fields when rows are selected in the UI
const rowsSelected = (name, rows) => {
  // Update the selected fields for the given type name
  selectedFields[name] = reactive(Object.values(rows))
}

// Helper function to build an array of fields for a given data type
const dataTypeFieldsBuilder = (dataType, allowComplex = false) =>
  dataType?.fields.filter((field) => {
    const fieldType = getFieldTypeLeaf(field.type)
    if (!fieldType || field.args.length > 0) return false // Exclude fields without a type or with arguments
    const { kind } = fieldType
    return (
      // Include scalar, enum, or object types (if 'allowComplex' is true)
      kind === 'SCALAR' || kind === 'ENUM' || (kind === 'OBJECT' && allowComplex)
    )
  })

// Computed property to build table data for selected fields, including complex types
const tableData = computed(() => {
  if (selectedQueryTypeName.value === '') return {}

  const complexFields = {}
  const processedTypes = new Set()

  // Recursive function to collect fields from complex types
  function collectComplexFields(typeName, SFs) {
    if (processedTypes.has(typeName)) return // Avoid processing the same type multiple times
    processedTypes.add(typeName)
    const dataType = typeLookup.value[typeName]
    if (!dataType || !SFs) return

    // Build the fields for the current data type
    complexFields[typeName] = dataTypeFieldsBuilder(dataType, true)

    // Iterate over selected fields to find nested complex types
    SFs.forEach((field) => {
      const fieldType = getFieldTypeLeaf(field.type)
      if (!fieldType) return

      if (fieldType.kind === 'OBJECT') {
        const subTypeName = fieldType.name ?? ''

        // Recursively collect fields from nested complex types
        collectComplexFields(subTypeName, selectedFields[subTypeName] || [])
      }
    })
  }

  // Start collecting fields from the root selected query type
  collectComplexFields(
    selectedQueryTypeName.value,
    selectedFields[selectedQueryTypeName.value ?? ''] || [],
  )

  return complexFields
})

// Computed property to create a lookup for complex field names
const complexTypeFieldLookup = computed(() =>
  Object.keys(tableData.value).reduce(
    (accumulator, key) => ({
      ...accumulator,
      [key]: tableData.value[key]
        .map((field) => [getFieldTypeLeaf(field.type), field])
        .filter(([type, _]) => type.kind === 'OBJECT') // Only include object types
        .reduce((acc, [type, field]) => ({ ...acc, [type.name]: field.name }), {}),
    }),
    {},
  ),
)

// Computed property to define variables for the GraphQL query
const variables = computed(() => ({
  first: 10,
  page: 1,
  accounts: selectedAccounts.value.map((acc) => acc.id),
}))

const typeRequiresIdOnQuery = (type) => {
  const dataType = typeLookup.value[type.name]
  if (!dataType) return false
  return (
    dataType.fields
      ?.map((field) => useFragment(FieldTypeFragmentDef, field))
      ?.some(
        (field) =>
          field.name === 'id' &&
          getFieldTypeLeaf(useFragment(TypeRefFragmentDef, field.type)).name === 'ID',
      ) ?? false
  )
}
// Helper function to build the selection set for a field in the GraphQL query
const fieldToQuery = (field, maxDepth = 5) => {
  if (maxDepth <= 0) return field.name // Stop recursion if max depth is reached
  const type = getFieldTypeLeaf(field.type)

  switch (type.kind) {
    case 'OBJECT':
      if (selectedFields[type.name ?? '']?.length > 0 || typeRequiresIdOnQuery(type)) {
        // For object types, recursively build the selection set
        return `${field.name} {
                ${typeRequiresIdOnQuery(type) ? 'id' : ''}
                ${selectedFields[type.name ?? '']?.map((f) => fieldToQuery(f, maxDepth - 1)).join('\n') ?? ''}
              }`
      }
      return ''
    case 'SCALAR':
    case 'ENUM':
      // For scalar or enum types, return the field name
      return field.name
  }
  // Default case, return the field name
  return field.name
}

const filtersOpen = ref(false)
const toggleFilters = () => {
  filtersOpen.value = !filtersOpen.value
}
// Computed property to dynamically build the GraphQL query based on selected fields and variables
const dynamicQuery = computed(() => {
  const selected = selectedQuery.value
  if (!selected) return ''
  const queryName: string = selected.value.name
  const supportedArgs = [
    ...new Set([...Object.keys(variables.value), ...Object.keys(activeFilterValues)]),
  ] // Here the magic should happen

  // Get the names of arguments accepted by the selected query
  const argNames = selected.value.args.map((arg) => useFragment(InputValueFragmentDef, arg).name)

  // Build the arguments string for the query
  const args = selected.value.args
    .map((arg) => {
      const argName = useFragment(InputValueFragmentDef, arg).name
      if (supportedArgs.includes(argName)) {
        return `${argName}: $${argName}`
      }
      return ``
    })
    .filter((q) => !!q) // Remove empty strings
    .join(', ')

  const argTypeStrs = selected.value.args
    .map((arg) => useFragment(InputValueFragmentDef, arg))
    .reduce((acc, arg) => {
      const argType = convertArgTypeToTypeStr(arg.type)
      return { ...acc, [arg.name]: argType }
    }, {})

  // Build the fields string for the query
  const fields = selectedFields[selectedQueryTypeName.value ?? '']
    ?.map((f) => fieldToQuery(f, 5))
    .join('\n')

  // Build the variables string for the query
  const vars = supportedArgs
    .filter((varName) => argNames.includes(varName))
    .map((varName) => `$${varName}: ${argTypeStrs[varName]}`)
    .join(', ')

  // Construct the complete GraphQL query
  const baseQuery = `
        query DataQuery(${vars}) {
          ${queryName}(${args}) {
            paginatorInfo {
              total
            }
            data {
              id
              ${fields}
            }
          }
        }
      `

  return baseQuery
})

// Reactive reference to indicate loading state of the query data
const loadingQueryData = ref(false)
// Reactive reference to store the result of the query data
const queryDataResult = ref([])

const queryVariables = computed(() => {
  const filters = Object.keys(activeFilterValues)
    .map((key) => [
      key,
      activeFilterValues[key]?.map?.(({ value }) => value) ?? activeFilterValues[key],
    ])
    .filter(
      // Filter out empty arrays and falsy values
      ([_, value]) =>
        (Array.isArray(value) && value.length > 0) ||
        (!Array.isArray(value) && !!value) ||
        value === false, // We are okay with the pure false value
    )
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})

  return { ...variables.value, ...filters }
})

// Function to execute the GraphQL query and fetch data
const queryHeaderData = async () => {
  const selected = selectedQuery.value
  if (!selected) return
  const queryName: string = selected.value.name

  const client = provider.clients[clientId]
  if (!client) return

  loadingQueryData.value = true // Set loading state to true

  // Execute the query using Apollo Client
  const { data } = await client.query({
    query: gql(dynamicQuery.value),
    variables: queryVariables.value,
  })

  // Store the query result data
  queryDataResult.value = data[queryName].data
  loadingQueryData.value = false // Set loading state to false
}

// Computed property to process and organize the query result data for display
const queryHeaderDataResults = computed(() =>
  Object.keys(selectedFields).reduce(
    (acc, val) => ({
      ...acc,
      [val]: uniqBy(
        queryDataResult.value.map((data: any & { __typename: string }) => {
          if (data?.__typename === val) return data

          // Access nested data using the complex type field lookup
          return data?.[complexTypeFieldLookup.value?.[data?.__typename]?.[val]] ?? {}
        }),
        'id', // Ensure uniqueness based on 'id'
      ),
    }),
    {},
  ),
)

// Helper function to get the render configuration for a field in the table
const getRenderFunction = (field, typeName) => {
  const leaf = getFieldTypeLeaf(field.type)

  // Return null if the field is not an object or ID type
  if (leaf.kind !== 'OBJECT' && leaf.name !== 'ID') return null

  // Base render configuration using 'TdText' component
  const base = {
    component: markRaw(TdText),
    componentProps: (row) => ({
      text: row && row[field.name] ? `${typeName}-${row[field.name]}` : null,
    }),
  }

  // Adjust the component props if the field is an object type
  if (leaf.kind === 'OBJECT') {
    base.componentProps = (row) => ({
      text: row && row[field.name] ? `${row[field.name].__typename}-${row[field.name].id}` : null,
    })
  }

  return base
}

// Computed property to define columns for the data table based on selected fields
const headerDataColumns = computed(() =>
  Object.keys(selectedFields).reduce(
    (acc, key) => ({
      ...acc,
      [key]: selectedFields[key].map((field) => ({
        title: fieldToReadAble(field.name),
        key: field.name,
        renderUsing: getRenderFunction(field, key),
      })),
    }),
    {},
  ),
)

// Reactive reference for the title of the export table
const tableTitle = ref('ExportTable')

// Function to export the query data, possibly to a CSV or Excel file
const exportData = async () => {
  const selected = selectedQuery.value
  if (!selected) return
  const queryName: string = selected.value.name

  const dataDescription: TableDescriber<any> = {
    title: tableTitle.value,
    sheets: [],
  }

  // Queue to process sheets for exporting nested data types
  const sheetQueue: any[][3] = [
    ['', selectedQueryTypeName.value, selectedFields[selectedQueryTypeName.value ?? '']],
  ]

  while (sheetQueue.length > 0) {
    const [path, sheetTitle, sheetFields] = sheetQueue.shift()

    const sheet: SheetDescriber<any> = {
      title: fieldToReadAble(sheetTitle),
      columns: [],
    }

    // Iterate over the fields to build columns for the export
    for (const field of sheetFields) {
      const leaf = getFieldTypeLeaf(field.type)
      const localPath = `${path}${field.name}`

      if (leaf.kind === 'OBJECT') {
        // For object types, create a column that references the nested object's ID
        const column = {
          title: fieldToReadAble(field.name),
          extraction: (row) => {
            if (row) {
              const data = dotAccess(row, localPath)
              return `${data?.__typename ?? ''}-${data?.id ?? ''}`
            }
            return ''
          },
        }
        sheet.columns.push(column)
        // If there are selected fields for the nested type, add it to the queue for further processing
        if (selectedFields.hasOwnProperty(leaf.name ?? '')) {
          sheetQueue.push([`${localPath}.`, leaf.name, selectedFields[leaf.name ?? '']])
        }
      } else {
        // For scalar types, create a column that extracts the value directly
        const column = {
          title: fieldToReadAble(field.name),
          extraction: (row) => {
            if (row) {
              return dotAccess(row, localPath) ?? ''
            }
            return ''
          },
        }
        sheet.columns.push(column)
      }
    }
    // Add the sheet to the data description for export
    dataDescription.sheets.push(sheet)
  }

  // Define query options for the export
  const queryOptions = {
    query: gql(dynamicQuery.value),
    variables: queryVariables.value,
  }

  // Define data mapping for pagination and list extraction
  const dataMap = {
    total: `${queryName}.paginatorInfo.total`,
    perPageVarName: 'first',
    pageVarName: 'page',
    listPath: `${queryName}.data`,
  }

  // Call the 'dataExport' function to perform the export
  await dataExport(dataDescription, queryOptions, dataMap, clientId)
}

const openRecurringExportModal = ref(false)

const schedule = ref('5 4 * * *')
const scheduleHumanReadable = computed(() => {
  try {
    return cronstrue.toString(schedule.value, { locale: currentLocale, verbose: true })
  } catch (_) {
    return $t('Invalid schedule')
  }
})

watch(dynamicQuery, () => {
  useQueryType(dynamicQuery.value)
})
</script>

<style lang="scss" scoped>
.field-table {
  margin-top: 3rem;
  &:deep(.table-wrap) {
    max-height: 300px;
    overflow: auto;
  }
}

.query-table {
  margin-top: 3rem;
  &:deep(.table-wrap) {
    overflow: auto;
  }
}
</style>
